// Router v5 config (ADR 0003): `/` → Leaderboard, `/stats` → IndustryStats,
// `/brands/:slug` → BrandDetail, `/brands/:slug/advisor` → Advisor and
// `/brands/:slug/lab` → Lab (sub-routes registered BEFORE the non-exact brands
// route so the Switch doesn't swallow them; the Lab additionally self-gates to
// the SESSION brand — contexts/SessionContext; the whole router sits behind
// components/AuthGate, which renders the PIN screen in its place while no
// workspace is open),
// `/compare/:a/:b` → Compare. Every screen's back control is
// `navigation/useBack` (go back where the reader came from, up to the
// screen's parent only when nothing of ours is behind) — the depth it reads
// is counted by the one history subscription below.
// Pages are code-split (React.lazy); the one Suspense fallback covering
// every route is the APP LOADER (components/AppLoader — the mark and the
// ring, shown only past its show-delay so a warm chunk never flashes it).
// Until 2026-10-15 it was the plain board's skeleton under the board's own
// header, a mirror of a screen a signed-in reader never lands on.
import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import AppLoader from "components/AppLoader";
import { resetGround } from "hooks/useGround";
import KeyboardHandoff from "navigation/KeyboardHandoff";
import { preloadable } from "navigation/preloadable";
import { trackBackDepth } from "navigation/useBack";

// the home is PRELOADABLE too (2026-10-01): the wall warms it while the
// reader types their code (components/AuthGate), so the gate opens — inside
// a view transition — on the home itself, not on a frame of its fallback
const { Component: Leaderboard, preload: preloadHome } = preloadable(
  () => import("pages/Leaderboard"),
);
export { preloadHome };
const IndustryStats = lazy(() => import("pages/IndustryStats"));
const BrandDetail = lazy(() => import("pages/BrandDetail"));
const Compare = lazy(() => import("pages/Compare"));
const Advisor = lazy(() => import("pages/Advisor"));
// the Lab is PRELOADABLE (navigation/preloadable): the demo home calls
// `preloadLab()` on mount, so the route renders synchronously inside the
// view transition that opens a question — `lazy` would show the board
// fallback for a frame first (user report 2026-09-11)
const { Component: Lab, preload: preloadLab } = preloadable(
  () => import("pages/Lab"),
);
export { preloadLab };

// The app's ONE scroll-to-top (2026-09-11, user report: "navigating from
// page to page sometimes doesn't load at the top"). Every PUSH — a new
// screen — opens at the top, in a LAYOUT effect on the location, so the
// reset lands before the first paint. Two things made the per-page effects
// that did this before ("sometimes") fail: they ran AFTER paint (a frame of
// the new screen at the old offset, then a jump), and they only ran once
// the page's chunk had loaded — the pages are lazy, so a cold route showed
// the Suspense fallback at the old offset first, and a screen with no
// effect of its own (the advisor) simply stayed there, its sticky header
// pinned over content that was already scrolled under it.
//
// It renders OUTSIDE the Suspense boundary (the same day, second report:
// "some content goes under the header"). Inside it, it was hidden along
// with the old page whenever a route suspended — React 18 disconnects the
// layout effects of a hidden tree — so on every cold chunk the fallback
// showed at the old offset, its own sticky header over skeleton rows that
// had scrolled under it, and only the page's arrival reset the window.
// Out here it runs in the very commit that shows the fallback.
//
// Only a PUSH. A REPLACE is view state, and the page that makes it owns
// the offset: the board's scope change stays put by design (user report
// 2026-09-08, "the layout jumps to top"), the stats filter and the compare
// swap reset themselves, the Lab clearing its `ask` is already at the top.
// A POP is left alone too: the board and the stats page restore their own
// remembered offsets on the way back (AppDataContext session memory — saved
// in LAYOUT-effect cleanups, which run in the commit's mutation phase,
// before this effect resets the window; a passive cleanup ran after it and
// remembered 0), the other screens reset themselves. `history.
// scrollRestoration` is `manual` (App.js), so the browser never re-applies
// an old offset either.
export function ScrollToTop() {
  const { key } = useLocation();
  const { action } = useHistory();
  useLayoutEffect(() => {
    if (action === "PUSH") window.scrollTo(0, 0);
  }, [key, action]);
  return null;
}

// Every ROUTE starts on paper (2026-09-13, user call: "make sure we don't
// add that background on any other page"): the gradient grounds — the
// green field, the violet dusk — and the status-bar tint that follows
// them belong to two views of the demo home and nowhere else. A screen
// that wants one declares it with hooks/useGround in its own commit; this
// resets the root and the chrome on every PATHNAME change first. Rendered
// BEFORE the Switch so its layout effect runs ahead of the arriving
// page's in the same commit (siblings in order), and outside Suspense so
// it runs in the commit that shows a cold chunk's fallback — which is
// therefore on paper too. The pathname, not the location key: a query
// change is view state (the lists' `?list=`) and the page that made it
// keeps its ground.
export function GroundReset() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    resetGround();
  }, [pathname]);
  return null;
}

export default function RouterConfig() {
  // one subscription for the whole app: it counts how deep into OUR history
  // the reader is, which is what every back control reads (navigation/useBack)
  const history = useHistory();
  useEffect(() => trackBackDepth(history), [history]);

  return (
    <>
      <ScrollToTop />
      <GroundReset />
      {/* the keyboard anchor a back control hands the keys to (see the
          component) — outside the routes so it survives the page swap */}
      <KeyboardHandoff />
      <Suspense fallback={<AppLoader late />}>
        <Switch>
          <Route exact path="/" component={Leaderboard} />
          <Route exact path="/stats" component={IndustryStats} />
          <Route exact path="/brands/:slug/advisor" component={Advisor} />
          <Route exact path="/brands/:slug/lab" component={Lab} />
          <Route path="/brands/:slug" component={BrandDetail} />
          <Route path="/compare/:a/:b" component={Compare} />
          {/* an unknown URL used to RENDER the board while the address bar
            kept the bad path — so a reload or a share sent the reader back
            to nothing, and the first filter silently rewrote the URL. It
            redirects now (a replace: the dead entry is not worth a back
            step) */}
          <Route>
            <Redirect to="/" />
          </Route>
        </Switch>
      </Suspense>
    </>
  );
}
